import { faXTwitter } from "@fortawesome/free-brands-svg-icons/faXTwitter";
import { faArrowUpRight } from "@fortawesome/pro-regular-svg-icons/faArrowUpRight";
import { faTrash } from "@fortawesome/pro-regular-svg-icons/faTrash";
import { faUpRightFromSquare } from "@fortawesome/pro-solid-svg-icons/faUpRightFromSquare";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Avatar, Grid, IconButton, Typography } from "@mui/material";
import { MprcCard, MprcChipChoice, MprcConfirmDialog, MprcContainer, MprcTypography } from "components";
import { MprcFeedTypography } from "components/MprcFeedTypography";
import { MprcRibbonScroll } from "components/MprcRibbonScroll";
import { GetPublicationAttachDto, PublicationAttachType } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import HtmlUtils from "lib/HtmlUtils";
import LibUtils from "lib/LibUtils";
import MediaUtils from "lib/MediaUtils";
import * as React from 'react';
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SessionService } from "services/sessionService";
import { isPresentationAttach } from "views/knowledge/publication/creation/components/Utils";
import { PublicationViewerDialog } from "views/knowledge/publication/dialogs/PublicationViewerDialog";

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

function getSourceUrlFromAttach(a: GetPublicationAttachDto) {
    if (a.file) {
        return a.file.url;
    }
    else if (a.linkMetadata) {
        if (a.attachType == PublicationAttachType.PerformaPublication)
            return `/Knowledge/${a.linkMetadata.type}/${a.linkMetadata.externalId}`;

        return a.linkMetadata.url;
    }
}

function getIconFromAttach(a: GetPublicationAttachDto, avatar?: boolean) {
    switch (a.attachType) {
        case PublicationAttachType.Document:
            if (a.file) return avatar
                ? <Avatar variant="rounded" src={MediaUtils.getFileIcon(a.file.metadata.filename)} sx={{ width: 24, height: 24, backgroundColor: "transparent" }} />
                : <img src={MediaUtils.getFileIcon(a.file.metadata.filename)} width={32} height={32} />;
            break;
        case PublicationAttachType.Twitter:
            return <FontAwesomeIcon icon={faXTwitter} color="#1D9BF0" />;
    }
    return <FontAwesomeIcon icon={faUpRightFromSquare} style={{ backgroundColor: "transparent", color: "#788898" }} />;
}

function getThumbnailFromAttach(a: GetPublicationAttachDto) {
    if (a.file) return <img src={MediaUtils.getFileIcon(a.file.metadata.filename)} width={55} />;
    if (a.linkMetadata?.thumbnailUrl) {
        if (a.attachType == PublicationAttachType.PerformaPublication && a.linkMetadata.thumbnailUrl != null) {
            return <img src={SessionService.current.getApiFullUrl(a.linkMetadata.thumbnailUrl)!} width={55} />;
        }

        return <img src={a.linkMetadata?.thumbnailUrl} width={55} />;
    }
    if (a.attachType == PublicationAttachType.Twitter) return <FontAwesomeIcon icon={faXTwitter} size="3x" color="#1D9BF0" />;
    return null;
}

function getTitleFromAttach(a: GetPublicationAttachDto) {
    if (a.file) return a.file.metadata.title ?? a.file.metadata.filename;
    return a.linkMetadata?.title;
}

function getDescriptionFromAttach(a: GetPublicationAttachDto) {
    if (a.file) {
        if (a.file.metadata.description)
            return a.file.metadata.description;

        if (a.attachType == PublicationAttachType.Image)
            return Formaters.fileSize(a.file?.metadata.fileSize) + " - " + Formaters.localeDate(a.file.metadata.fileCreationDate);
    }
    return a.linkMetadata?.description;
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IRenderProps {
    attach: PublicationUnifiedAttach;
    onDelete: (id: string) => void;
    onView: (e: React.SyntheticEvent, item: PublicationUnifiedAttach) => void;
}

const RenderSingle: React.FC<IRenderProps> = ({ attach, onDelete, onView }) => {
    return (
        <MprcContainer>
            <MprcCard paperColor={["paper", "dark"]} elevation={0} sx={{ p: 1 }}>

                <Grid container justifyContent="center" alignItems="flex-start" spacing={2}>
                    {
                        attach.thumbnail &&
                        <Grid alignSelf="center">
                            {attach.thumbnail}
                        </Grid>
                    }
                    <Grid size="grow">
                        <Grid container justifyContent="center" alignItems="start">
                            {
                                <Grid size="grow">
                                    {attach.title &&
                                        <MprcFeedTypography variant="body3" title={attach.title}>
                                            {attach.title}
                                        </MprcFeedTypography>
                                    }
                                    {attach.description &&
                                        <Grid size="grow">
                                            <MprcTypography noWrapRows={2} variant="subtitle2" component="div">
                                                {attach.description}
                                            </MprcTypography>
                                        </Grid>
                                    }
                                </Grid>
                            }
                            <Grid>
                                <IconButton size="small" onClick={() => onDelete(attach.attachId)}>
                                    <FontAwesomeIcon icon={faTrash} fixedWidth />
                                </IconButton>
                                <IconButton size="small" onClick={(e) => onView(e, attach)}>
                                    <FontAwesomeIcon icon={faArrowUpRight} fixedWidth />
                                </IconButton>
                            </Grid>

                            {
                                attach.title == null && attach.description != null &&
                                <Grid size="grow">
                                    <Typography variant="subtitle2">
                                        {attach.description}
                                    </Typography>
                                </Grid>
                            }
                        </Grid>
                    </Grid>
                </Grid>
            </MprcCard>
        </MprcContainer>
    );
};

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IRenderAttachsProps {
    attachs: PublicationUnifiedAttach[];
    onDelete: (id: string) => void;
    onView: (e: React.SyntheticEvent, item: PublicationUnifiedAttach) => void;
}

const RenderAttachs: React.FC<IRenderAttachsProps> = ({ attachs, onDelete, onView }) => {

    return attachs.length > 0
        ? (
            <MprcRibbonScroll>
                {
                    attachs.map((v, index) => (
                        <MprcChipChoice
                            key={index}
                            avatar={v.icon}
                            label={LibUtils.safeSubstr(v.title, 0, 30, '...')}
                            title={v.title}
                            onDelete={() => onDelete(v.attachId)}
                            onClick={(e) => onView(e, v)}
                        />
                    ))
                }
            </MprcRibbonScroll>
        )
        : null;
};

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    forceAttachsInList?: boolean;
    attachs: GetPublicationAttachDto[];
    onChange: (data: GetPublicationAttachDto[]) => void;
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const PublicationAttachView: React.FC<IProps> = ({ attachs, forceAttachsInList = false, onChange }) => {
    const { t } = useTranslation("knowledge");
    const [dialog, setDialog] = useState<any>(null);


    ////////////////////////////////////////////////

    const handleAttachDelete = (attachId: string) => {
        setDialog(
            <MprcConfirmDialog
                open
                severity="danger"
                message={<span>{t("Publication.Attach.ConfirmRemoveMessage")}</span>}
                onClose={(result) => {
                    setDialog(null);
                    if (result) onChange(attachs.filter(p => p.attachId != attachId));
                }}
            />
        );
    };

    const handleViewAttach = (e: React.SyntheticEvent, attach: PublicationUnifiedAttach) => {
        e.preventDefault();

        if (attach.attachType == PublicationAttachType.PerformaPublication && attach.objectId) {
            setDialog(
                <PublicationViewerDialog
                    publicationId={attach.objectId}
                    onClose={() => setDialog(null)}
                />
            );
        } else {
            if (attach.filename) MediaUtils.openFileViewerOrDownloadUrl(attach.filename, attach.sourceUrl, attach.fileId);
            else HtmlUtils.openUrlInNewWindow(attach.sourceUrl, attach.attachId, { noopener: false, noreferrer: false });
        }

    };

    const memoizedAttachs = useMemo(() => {

        const localAttachs = attachs.filter(p => !isPresentationAttach(p.attachType));

        const mappedAttachs: PublicationUnifiedAttach[] =
            localAttachs
                .map((v, index) => ({
                    arrIndex: index,
                    attachId: v.attachId,
                    sourceUrl: getSourceUrlFromAttach(v),
                    fileId: v.file?.fileId,
                    filename: v.file ? v.file.metadata.filename : undefined,
                    title: getTitleFromAttach(v),
                    description: getDescriptionFromAttach(v),
                    thumbnail: getThumbnailFromAttach(v),
                    icon: getIconFromAttach(v, true),
                    attachType: v.attachType,
                    objectId: v.linkMetadata?.externalId,
                } as PublicationUnifiedAttach))
            ;

        return localAttachs.length == 0
            ? null
            : localAttachs.length == 1 && !forceAttachsInList
                ? <RenderSingle attach={mappedAttachs[0]} onDelete={handleAttachDelete} onView={handleViewAttach} />
                : <RenderAttachs attachs={mappedAttachs} onDelete={handleAttachDelete} onView={handleViewAttach} />
            ;
    }, [attachs]);

    ////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////
    return (
        <>
            {dialog}
            {memoizedAttachs}
        </>
    );
};
